import { z } from "zod";

/** Source kinds, as the Memory board lists the project's knowledge inputs. */
export const memorySourceKinds = ["repository", "documents", "manual"] as const;
export const memorySourceKindSchema = z.enum(memorySourceKinds);
export type MemorySourceKind = z.infer<typeof memorySourceKindSchema>;

/** Indexing lifecycle for a memory source. Status is always shown as a labelled badge. */
export const memoryIndexStates = ["pending", "indexing", "ready", "failed"] as const;
export const memoryIndexStateSchema = z.enum(memoryIndexStates);
export type MemoryIndexState = z.infer<typeof memoryIndexStateSchema>;

/** One project-scoped source in GET /memory/sources. */
export const memorySourceSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1),
  kind: memorySourceKindSchema,
  state: memoryIndexStateSchema,
  itemCount: z.number().int().min(0),
  lastSyncAt: z.iso.datetime().nullable(),
});
export type MemorySource = z.infer<typeof memorySourceSchema>;

export const memorySourceListSchema = z.array(memorySourceSchema);
export type MemorySourceList = z.infer<typeof memorySourceListSchema>;

/** Adds a source by reference. Raw credentials or secret values are never accepted. */
export const createMemorySourceSchema = z.object({
  name: z.string().trim().min(1).max(200),
  kind: memorySourceKindSchema,
  reference: z.string().trim().min(1).max(500),
});
export type CreateMemorySource = z.infer<typeof createMemorySourceSchema>;

/** A recalled or manually added memory entry. The source is always inside the same project. */
export const memoryEntrySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  sourceId: z.uuid(),
  body: z.string().min(1).max(4000),
  ref: z.string().nullable(),
  runsUsed: z.number().int().min(0),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type MemoryEntry = z.infer<typeof memoryEntrySchema>;

export const memoryEntryListSchema = z.array(memoryEntrySchema);
export type MemoryEntryList = z.infer<typeof memoryEntryListSchema>;

/** A bounded read of the project's stored manual facts, newest first. */
export const listMemoryEntriesSchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ListMemoryEntries = z.infer<typeof listMemoryEntriesSchema>;

/** A phrase a run would recall, scoped by the route's project id. */
export const recallMemorySchema = z.object({
  phrase: z.string().trim().min(1).max(1000),
  limit: z.number().int().min(1).max(20).optional().default(5),
});
export type RecallMemory = z.infer<typeof recallMemorySchema>;

export const addMemoryFactSchema = z.object({
  body: z.string().trim().min(1).max(4000),
  ref: z.string().trim().max(500).nullable().optional(),
});
export type AddMemoryFact = z.infer<typeof addMemoryFactSchema>;

export const updateMemoryFactSchema = z.object({
  body: z.string().trim().min(1).max(4000),
  ref: z.string().trim().max(500).nullable().optional(),
});
export type UpdateMemoryFact = z.infer<typeof updateMemoryFactSchema>;

/** LightRAG dual-level Knowledge Graph node (Concept vs Entity). */
export const memoryGraphNodeTypeSchema = z.enum(["concept", "entity"]);
export type MemoryGraphNodeType = z.infer<typeof memoryGraphNodeTypeSchema>;

export const memoryGraphNodeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  type: memoryGraphNodeTypeSchema,
  description: z.string().default(""),
  factCount: z.number().int().min(0).default(0),
  source: z.string().optional(),
});
export type MemoryGraphNode = z.infer<typeof memoryGraphNodeSchema>;

/** LightRAG Knowledge Graph relation edge connecting concepts and entities. */
export const memoryGraphEdgeSchema = z.object({
  id: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
  relation: z.string().min(1),
  weight: z.number().min(0).default(1),
});
export type MemoryGraphEdge = z.infer<typeof memoryGraphEdgeSchema>;

export const memoryGraphSchema = z.object({
  nodes: z.array(memoryGraphNodeSchema),
  edges: z.array(memoryGraphEdgeSchema),
});
export type MemoryGraph = z.infer<typeof memoryGraphSchema>;

/**
 * `POST /api/v1/projects/:id/memory/service-recall`: what the shared memory service recalls for the
 * project (agent task summaries and remembered facts). 503 when the service is not configured.
 */
export const serviceMemorySchema = z.object({
  id: z.string().min(1),
  memoryType: z.string(),
  content: z.string(),
  summary: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export type ServiceMemory = z.infer<typeof serviceMemorySchema>;
export const serviceMemoryListSchema = z.array(serviceMemorySchema);
